import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';

// Cloud Functions emulator → production fallback.
//
// When a caller targets a localhost emulator URL but the emulator isn't
// running (or doesn't have that function loaded), the fetch fails silently
// and the calling page lands in a confusing empty/error state. Instead, we
// transparently retry against the equivalent production URL and remember
// the emulator is dead for the rest of the session, so subsequent calls
// skip the emulator entirely.
//
// Prod URL is derived from the emulator URL via the Cloud Functions Gen-2
// hostname pattern used by this GCP project:
//   http://localhost:5001/<project>/<region>/<funcName>
//     → https://<funcname-lowercased>-tilln6moiq-uc.a.run.app
//
// Devs who want strict emulator-only behavior should stop the dev server
// rather than rely on this fallback masking real bugs — fallback only fires
// after the first emulator-side failure.

const LOCALHOST_RE = /^https?:\/\/(?:localhost|127\.0\.0\.1):\d+\/[^/]+\/[^/]+\/([^/?#]+)/;
const PROD_HOST_SUFFIX = '-tilln6moiq-uc.a.run.app';

let emulatorDead = false;

// Probe localhost:5001 once at module load. If the emulator port isn't
// listening, mark dead BEFORE any function call fires — so the page doesn't
// spam the console with "Could not connect to the server" / "Failed to load
// resource" for every emulator URL on first paint. The browser itself logs
// those (we can't suppress them from JS), so the only way to keep the console
// clean is to not make the doomed fetches in the first place.
//
// 400ms is enough for an ECONNREFUSED on a same-host port. If the emulator
// IS running it answers in ~5ms. If the user is somewhere with a hostile
// network shim that holds the connection open we bail anyway — that just
// means we use prod, which is the safer default.
const probeEmulator = (() => {
	if (typeof window === 'undefined' || typeof fetch === 'undefined') {
		return Promise.resolve();
	}
	if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
		// In prod the env vars don't point at localhost anyway; no probe needed.
		return Promise.resolve();
	}
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), 400);
	return fetch('http://localhost:5001', { method: 'GET', signal: controller.signal })
		.then(() => { clearTimeout(timer); /* any response = emulator alive */ })
		.catch(() => { clearTimeout(timer); emulatorDead = true; });
})();

const deriveProdUrl = (emulatorUrl) => {
	const match = emulatorUrl.match(LOCALHOST_RE);
	if (!match) return null;
	const funcName = match[1];
	return `https://${funcName.toLowerCase()}${PROD_HOST_SUFFIX}`;
};

const isEmulatorUrl = (url) => typeof url === 'string' && LOCALHOST_RE.test(url);

// Treat any non-200 from the emulator as "emulator side failed" — matches the
// existing per-call fallback convention in src/functions/arv/index.js so devs
// see consistent behavior across the app. Network errors (TypeError) are
// already coerced to status 406 by rawFetch below. Status 0 = caller aborted
// the request via AbortSignal; we must NOT fall back, since (a) the caller
// doesn't want results, and (b) doing so would falsely trip emulatorDead.
const shouldFallback = (response) => !response || (response.status !== 200 && response.status !== 0);

const rawFetch = (url, body, signal, extraHeaders = {}) =>
	new Promise((resolve) => {
		fetch(url, {
			method: 'POST',
			mode: 'cors',
			headers: { 'Content-Type': 'application/json', ...extraHeaders },
			body: JSON.stringify(body),
			signal
		})
			.then(async (response) => {
				try {
					resolve(await response.json());
				} catch {
					resolve({ status: response.status || 500, error: 'Invalid JSON response' });
				}
			})
			.catch((error) => {
				if (error?.name === 'AbortError') {
					resolve({ status: 0, error: 'Aborted' });
				} else if (error instanceof TypeError) {
					resolve({ status: 406, error: 'Load failed - network error' });
				} else {
					resolve({ status: 500, error: error.message || 'Unknown fetch error' });
				}
			});
	});

const callWithEmulatorFallback = async (url, body, signal, extraHeaders = {}) => {
	// If this is an emulator URL and the startup probe hasn't resolved yet,
	// wait. The probe takes ≤400ms when dead, ~5ms when alive — short enough
	// to be invisible vs the round-trip itself, and worth it to keep the
	// console clean (no "Failed to load resource" for fetches we KNOW will
	// fail). Non-emulator URLs skip the await entirely.
	if (isEmulatorUrl(url)) await probeEmulator;

	if (isEmulatorUrl(url) && emulatorDead) {
		const prodUrl = deriveProdUrl(url);
		if (prodUrl) return rawFetch(prodUrl, body, signal, extraHeaders);
	}

	const response = await rawFetch(url, body, signal, extraHeaders);

	if (isEmulatorUrl(url) && shouldFallback(response)) {
		const prodUrl = deriveProdUrl(url);
		if (prodUrl) {
			if (!emulatorDead) {
				emulatorDead = true;
				console.warn(
					`[cloudFunctionV2] Emulator unreachable (${url} → ${response?.status}). ` +
						`Falling back to production (${prodUrl}) for the rest of this session.`
				);
			}
			return rawFetch(prodUrl, body, signal, extraHeaders);
		}
	}

	return response;
};

export const cloudFunctionV2 = (url, data, options = {}) =>
	callWithEmulatorFallback(url, { data }, options.signal);

export const cloudFunctionV2Body = (url, data, options = {}) =>
	callWithEmulatorFallback(url, data, options.signal);

// Authenticated variant of cloudFunctionV2: attaches the caller's Firebase ID
// token as `Authorization: Bearer <token>` so onRequest endpoints can verify the
// session and derive companyId from it (instead of trusting a body value). Use
// this for the CRM endpoints guarded by verifyCompanyAuth. Returns a 401-shaped
// result if there's no signed-in user rather than firing an unauthenticated call.
export const cloudFunctionV2Auth = async (url, data, options = {}) => {
	let idToken = null;
	try {
		idToken = await getAuth().currentUser?.getIdToken?.();
	} catch (err) {
		// fall through to the no-token guard below
	}
	if (!idToken) {
		return { status: 401, error: 'Not authenticated' };
	}
	return callWithEmulatorFallback(url, { data }, options.signal, { Authorization: `Bearer ${idToken}` });
};

let callableFunctions = null;
let callableEmulatorConnected = false;

const getCallableFunctions = () => {
	const fns = callableFunctions || getFunctions();
	callableFunctions = fns;
	if (
		!callableEmulatorConnected &&
		process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === 'true' &&
		typeof window !== 'undefined' &&
		(window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
	) {
		try { connectFunctionsEmulator(fns, '127.0.0.1', 5001); } catch (e) {}
		callableEmulatorConnected = true;
	}
	return fns;
};

export const callFirebaseFunction = async (name, data = {}, options = {}) => {
	try {
		const callable = httpsCallable(getCallableFunctions(), name, options);
		const result = await callable(data);
		return result.data;
	} catch (error) {
		return {
			status: 500,
			error: error?.message || 'Callable request failed',
			code: error?.code || null
		};
	}
};

/**
 * Get the count of MLS properties matching the given filters
 * Uses the existing awsQuery Cloud Function with type: "mlsPropertyCount"
 * @param {string} countyId - 5-digit FIPS county code
 * @param {Object} filters - Filter criteria (priceMin, priceMax, bedsMin, etc.)
 * @returns {Promise<{status: number, count?: number, error?: string}>}
 */
export const getMlsPropertyCount = async (countyId, filters = {}) => {
	const url = process.env.REACT_APP_AWS_QUERY_URL;
	if (!url) {
		console.error('REACT_APP_AWS_QUERY_URL not configured');
		return { status: 500, error: 'AWS Query URL not configured' };
	}
	return await cloudFunctionV2(url, {
		type: 'mlsPropertyCount',
		countyId,
		filters
	});
};

/**
 * Mint (or reuse) a short `app.clozers.co/d/{code}` link for a deal, used by the
 * copy-a-link helpers so the pasted URL is short instead of the ~100-char
 * /deals/{slug}/{leadId}/{buyerId} form. Authenticated: companySlug is derived
 * from the caller's session server-side. Returns { status, data: { shortUrl } }.
 * @param {string} leadId
 * @param {string} [buyerId] - omit for the anonymous (buyer-less) deal link
 */
export const getDealShortLink = async (leadId, buyerId) => {
	const url = process.env.REACT_APP_GET_DEAL_SHORT_LINK;
	if (!url) {
		console.error('REACT_APP_GET_DEAL_SHORT_LINK not configured');
		return { status: 500, error: 'Deal short-link URL not configured' };
	}
	return await cloudFunctionV2Auth(url, { leadId, buyerId });
};
